import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import logoBranco from '../../assets/img/logo-branco.png';
import logoPng from '../../assets/img/logo.png';

interface MainNavigationProps {
  isMobile?: boolean;
  isMenuOpen?: boolean;
  toggleMenu?: () => void;
  scrollPosition?: number;
  logoImage?: string;
  blogImage?: string;
  handleScheduleClick?: () => void;
}

const MainNavigation: React.FC<MainNavigationProps> = ({
  isMobile,
  isMenuOpen,
  toggleMenu,
  scrollPosition,
  logoImage,
  handleScheduleClick,
}) => {
  const location = useLocation();
  const [internalIsMobile, setInternalIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [internalIsMenuOpen, setInternalIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');

  const resolvedIsMobile = isMobile ?? internalIsMobile;
  const resolvedIsMenuOpen = isMenuOpen ?? internalIsMenuOpen;
  const resolvedScrollPosition = scrollPosition ?? 0;
  const isScrolled = resolvedScrollPosition > 50;
  const resolvedLogoImage = logoImage ?? logoBranco;
  const scrolledLogoImage = logoPng;
  const menuItemClass = isScrolled
    ? 'px-3 py-2 rounded-md transition-all duration-300 hover:bg-[#2a3c5c]/10 hover:font-bold'
    : 'px-3 py-2 rounded-md transition-all duration-300 hover:bg-white/20 hover:font-bold';
  const scheduleButtonClass = isScrolled
    ? 'rounded-md bg-[#2a3c5c] text-white px-4 py-2 font-semibold transition duration-500 hover:bg-[#1f2f4c]'
    : 'rounded-md bg-white text-[#1f2f4c] px-4 py-2 font-semibold transition duration-500 hover:bg-[#eef3ff]';

  const fallbackToggleMenu = useCallback(() => {
    setInternalIsMenuOpen((current) => !current);
  }, []);

  const resolvedToggleMenu = useMemo(() => {
    return toggleMenu ?? fallbackToggleMenu;
  }, [toggleMenu, fallbackToggleMenu]);

  const handleBlogClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = '/#blog';
      return;
    }
    document.getElementById('blog')?.scrollIntoView({ behavior: 'smooth' });
  }, [location.pathname]);

  const resolvedScheduleClick = useCallback(() => {
    if (handleScheduleClick) {
      handleScheduleClick();
      return;
    }
    if (location.pathname !== '/') {
      window.location.href = '/#agendamento';
      return;
    }
    document.getElementById('agendamento')?.scrollIntoView({ behavior: 'smooth' });
  }, [handleScheduleClick, location.pathname]);

  const handleHomeClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  const handleLogoClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const getMenuItemClassName = useCallback((sectionKey: string) => {
    const activeClass = isScrolled
      ? 'font-bold text-[#1e2f50] bg-[#2a3c5c]/12'
      : 'font-bold text-white bg-white/20';
    return `${menuItemClass} ${activeSection === sectionKey ? activeClass : ''}`;
  }, [activeSection, menuItemClass, isScrolled]);

  useEffect(() => {
    if (typeof isMobile !== 'undefined') {
      return;
    }
    const handleResize = () => {
      setInternalIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  const isMenuOpenRef = useRef(resolvedIsMenuOpen);
  isMenuOpenRef.current = resolvedIsMenuOpen;
  const toggleMenuRef = useRef(resolvedToggleMenu);
  toggleMenuRef.current = resolvedToggleMenu;

  useEffect(() => {
    if (isMenuOpenRef.current) {
      toggleMenuRef.current();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const sectionIds = ['sobre', 'servicos', 'como-funciona', 'depoimentos', 'contato'];
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element));

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries.length > 0) {
          setActiveSection(visibleEntries[0].target.id);
        }
      },
      { root: null, rootMargin: '-40% 0px -45% 0px', threshold: [0.1, 0.25, 0.4, 0.6] },
    );

    sections.forEach((section) => observer.observe(section));

    const handleWindowScroll = () => {
      if (window.scrollY < 60) {
        setActiveSection('inicio');
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    handleWindowScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [location.pathname]);

  return (
    <nav
      className={`fixed w-full z-50 top-0 transition-all duration-500 ${isScrolled ? 'bg-white/40 text-[#2a3c5c] backdrop-blur-md shadow-[0_10px_24px_rgba(20,32,56,0.12)]' : 'bg-[#111c32]/20 text-white backdrop-blur-sm'}`}
      role="navigation"
      aria-label="Menu principal"
      style={{ width: '100vw', maxWidth: '100%', left: '0', right: '0', overflowX: 'hidden' }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between h-16 w-full">
          <div className="flex-shrink-0">
            <Link to="/" onClick={handleLogoClick} className="flex items-center h-16 relative" aria-label="Ir para página inicial">
              <img
                src={resolvedLogoImage}
                alt="EFortiPsi - Logo Eliane Forti Fernandes Psicóloga"
                width="64"
                height="64"
                className={`w-auto transition-all duration-500 ease-in-out absolute left-0 ${isScrolled ? 'h-11 opacity-0' : 'h-16 opacity-100'}`}
              />
              <img
                src={scrolledLogoImage}
                alt="EFortiPsi - Logo Eliane Forti Fernandes Psicóloga"
                width="64"
                height="64"
                className={`w-auto transition-all duration-500 ease-in-out ${isScrolled ? 'h-11 opacity-100' : 'h-16 opacity-0'}`}
              />
            </Link>
          </div>
          {!resolvedIsMobile && (
            <div className="ml-10 flex items-baseline space-x-4" role="menubar">
              <Link to="/" onClick={handleHomeClick} className={getMenuItemClassName('inicio')} role="menuitem">Início</Link>
              <a href="/#sobre" className={getMenuItemClassName('sobre')} role="menuitem">Sobre</a>
              <a href="/#servicos" className={getMenuItemClassName('servicos')} role="menuitem">Serviços</a>
              <a href="/#como-funciona" className={getMenuItemClassName('como-funciona')} role="menuitem">Como funciona</a>
              <a href="/#depoimentos" className={getMenuItemClassName('depoimentos')} role="menuitem">Depoimentos</a>
              <a
                href="/#blog"
                onClick={handleBlogClick}
                className={getMenuItemClassName('blog')}
                role="menuitem"
              >
                Blog
              </a>
              <a
                href="/#contato"
                className={scheduleButtonClass}
                role="menuitem"
                onClick={resolvedScheduleClick}
              >
                Agendar consulta
              </a>
            </div>
          )}
          {resolvedIsMobile && (
            <button
              onClick={resolvedToggleMenu}
              className="p-2 flex items-center justify-center"
              aria-expanded={resolvedIsMenuOpen}
              aria-label={resolvedIsMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              {resolvedIsMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>
      {resolvedIsMobile && resolvedIsMenuOpen && (
        <div role="menu" aria-label="Menu mobile" className={`${isScrolled ? 'bg-white/45 backdrop-blur-md' : 'bg-white/35 backdrop-blur-sm'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/" onClick={(e) => { handleHomeClick(e); resolvedToggleMenu(); }} className={`block ${getMenuItemClassName('inicio')}`} role="menuitem">Início</Link>
            <a href="/#sobre" onClick={resolvedToggleMenu} className={`block ${getMenuItemClassName('sobre')}`} role="menuitem">Sobre</a>
            <a href="/#servicos" onClick={resolvedToggleMenu} className={`block ${getMenuItemClassName('servicos')}`} role="menuitem">Serviços</a>
            <a href="/#como-funciona" onClick={resolvedToggleMenu} className={`block ${getMenuItemClassName('como-funciona')}`} role="menuitem">Como funciona</a>
            <a href="/#depoimentos" onClick={resolvedToggleMenu} className={`block ${getMenuItemClassName('depoimentos')}`} role="menuitem">Depoimentos</a>
            <a
              href="/#blog"
              onClick={(e) => { handleBlogClick(e); resolvedToggleMenu(); }}
              className={`block ${getMenuItemClassName('blog')}`}
              role="menuitem"
            >
              Blog
            </a>
            <a
              href="/#contato"
              className={`mt-2 block ${scheduleButtonClass}`}
              role="menuitem"
              onClick={() => { resolvedScheduleClick(); resolvedToggleMenu(); }}
            >
              Agendar consulta
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MainNavigation;
